/**
 * TOP-500 — una fila de producto dentro de un subgrupo del árbol.
 *
 * Es de presentación: no pide nada al servidor ni decide nada. Lo que sale caro de
 * calcular (si el usuario puede ver importes, cómo se llama cada cuenta) le llega por
 * propiedades, para no repetirlo doscientas veces por página.
 *
 * Reglas de la fila (acordadas en las notas de la tarea):
 *  - La casilla se puede marcar SOLO si el producto está disponible para esta cuenta y
 *    hay con qué encolarlo: o tiene un producto de Amazon asociado, o quedó en empate
 *    (varios productos posibles), que el servidor sabe resolver solo. Un producto ya
 *    publicado o ya en la cola no se vuelve a encolar, y uno sin nada que encolar tampoco.
 *    Antes los empates se mostraban como "Sin código Amazon" y NO se podían marcar, pero
 *    el que marcaba el subgrupo entero se los llevaba igual: son 6.322 filas, el 24 % de
 *    la planilla.
 *  - La fila se muestra apagada (`opacity-60`) cuando la cuenta ya lo tiene.
 *  - "Familia ×N" solo con N ≥ 2: un producto suelto no es una familia.
 *  - El precio pasa por el permiso de importes; sin permiso se muestra un guion.
 */
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '../../../components/shared/StatusBadge';
import { SITE_FLAGS } from '../shared';

/** Estado del candidato para la cuenta elegida (espejo de `OwnState` del servidor). */
export type Top500State = 'disponible' | 'en_cola' | 'publicado';

/** Un producto del nodo, tal como lo devuelve `GET /publisher/top500/node`. */
export interface Top500Product {
  id: number;
  gtin: string;
  ml_title: string;
  amz_title: string | null;
  asin: string | null;
  amz_price: number | null;
  resolve_status: string;
  countries: string[];
  best_ranking: number;
  permalink: string | null;
  main_image: string | null;
  monthly_sold: number | null;
  state: Top500State;
  via: string | null;
  skipped?: { status: 'skipped_filter' | 'skipped_noise'; reason: string | null };
  family_size: number | null;
  other_sellers: number[];
}

interface Props {
  product: Top500Product;
  selected: boolean;
  onToggle: (id: number, selected: boolean) => void;
  /** ¿Puede el usuario ver importes? Viene calculado de arriba (una vez por lista). */
  canMoney: boolean;
  /** id de cuenta → nombre visible, para "También en …". */
  sellerNames: Map<number, string>;
}

/** Empate: el buscador encontró varios productos de Amazon posibles para esta fila. */
function esEmpate(p: Top500Product): boolean {
  return p.resolve_status === 'ambiguous';
}

/**
 * Se puede elegir si está disponible para la cuenta y hay con qué encolarlo: producto de
 * Amazon resuelto, o empate (el servidor elige entre las opciones y decide si lo encola).
 */
export function esSeleccionable(p: Top500Product): boolean {
  return p.state === 'disponible' && (!!p.asin || esEmpate(p));
}

export function ProductRow({ product: p, selected, onToggle, canMoney, sellerNames }: Props) {
  const { t } = useTranslation();
  const yaCargado = p.state !== 'disponible';
  const seleccionable = esSeleccionable(p);

  // Motivos del descarte previo, ya traducidos ("precio,peso" → "precio, peso").
  const motivos = (p.skipped?.reason ?? '')
    .split(',')
    .map((r) => r.trim())
    .filter(Boolean)
    .map((r) => t(`pub.top500.reasons.${r}`, r))
    .join(', ');

  const otras = p.other_sellers.map((id) => sellerNames.get(id) ?? `#${id}`);

  return (
    <div
      className={`flex items-start gap-3 px-3 py-2 border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-tertiary)] ${
        yaCargado ? 'opacity-60' : ''
      }`}
    >
      <input
        type="checkbox"
        checked={selected}
        disabled={!seleccionable}
        onChange={(e) => onToggle(p.id, e.target.checked)}
        aria-label={t('pub.top500.selectProductLabel', { name: p.ml_title })}
        className="mt-1 flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
      />

      {p.main_image ? (
        <img
          src={p.main_image}
          alt=""
          loading="lazy"
          className="w-10 h-10 rounded object-cover flex-shrink-0 bg-[var(--color-bg-tertiary)]"
        />
      ) : (
        <div className="w-10 h-10 rounded flex-shrink-0 bg-[var(--color-bg-tertiary)]" />
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] text-[var(--color-text-tertiary)] flex-shrink-0">
            {p.best_ranking > 0 ? `#${p.best_ranking}` : '—'}
          </span>
          {p.permalink ? (
            <a
              href={p.permalink}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-[var(--color-text)] hover:underline truncate"
              title={p.ml_title}
            >
              {p.ml_title}
            </a>
          ) : (
            <span className="text-xs text-[var(--color-text)] truncate" title={p.ml_title}>
              {p.ml_title}
            </span>
          )}
        </div>

        {p.amz_title && (
          <p className="text-[11px] text-[var(--color-text-secondary)] truncate" title={p.amz_title}>
            {p.amz_title}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2 mt-1">
          {p.countries.length > 0 && (
            <span className="text-xs" title={p.countries.join(', ')}>
              {p.countries.map((c) => SITE_FLAGS[c] || '\u{1F310}').join(' ')}
            </span>
          )}

          {p.family_size !== null && p.family_size >= 2 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--color-border-light)] text-[var(--color-text-secondary)]">
              {t('pub.top500.family')} {'×'}
              {p.family_size}
            </span>
          )}

          {otras.length > 0 && (
            <span className="text-[10px] text-[var(--color-text-tertiary)]">
              {t('pub.top500.alsoIn')} {otras.join('/')}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col items-end gap-1 flex-shrink-0 text-right">
        <span className="text-xs text-[var(--color-text)]">
          {/* El importe puede llegar filtrado por permisos: se muestra sólo si ES un número. */}
          {canMoney && typeof p.amz_price === 'number' ? `$${p.amz_price.toFixed(2)}` : '—'}
        </span>
        <span className="text-[10px] text-[var(--color-text-tertiary)] whitespace-nowrap">
          {p.monthly_sold !== null
            ? t('pub.top500.perMonth', { n: p.monthly_sold })
            : '—'}
        </span>
      </div>

      <div className="flex-shrink-0">
        {p.state === 'publicado' ? (
          <StatusBadge status="unknown" label={t('pub.top500.published')} size="sm" />
        ) : p.state === 'en_cola' ? (
          <StatusBadge status="unknown" label={t('pub.top500.inQueue')} size="sm" />
        ) : !p.asin && esEmpate(p) ? (
          <StatusBadge status="info" label={t('pub.top500.ambiguous')} size="sm" />
        ) : !p.asin ? (
          <StatusBadge status="info" label={t('pub.top500.noAsin')} size="sm" />
        ) : p.skipped ? (
          <StatusBadge status="warning" label={motivos || t('pub.top500.reasons.ruido')} size="sm" />
        ) : null}
      </div>
    </div>
  );
}
